import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import { selectCurrentDate } from './selectors';
import { setCurrentDate } from './slice';

const STORAGE_KEY = 'appointments_currentDate_timestamp';
const EXPIRY_MS = 15 * 60 * 1000; // 15 minutes

/**
 * Hook that checks if currentDate has expired on component mount
 * and resets it to today if needed, while preserving user preferences.
 *
 * Uses localStorage to track when currentDate was last set,
 * independent of redux-persist. This ensures users see relevant
 * appointment data when navigating back to the schedule after
 * an extended period.
 *
 * Behavior:
 * - On mount: Checks age of currentDate, resets if >15 minutes old
 * - On date change: Updates timestamp to prevent future expiration
 * - Does NOT trigger on window focus (preserves user context)
 *
 * @example
 * ```tsx
 * export const ScheduleView = () => {
 *   useCurrentDateExpiration(); // Call at top of component
 *   // ... rest of component
 * }
 * ```
 */
export function useCurrentDateExpiration() {
  const dispatch = useAppDispatch();
  const currentDate = useAppSelector(selectCurrentDate);

  // Check expiration on mount only
  useEffect(() => {
    const storedTimestamp = localStorage.getItem(STORAGE_KEY);

    if (!storedTimestamp) {
      // First visit or timestamp was cleared, initialize
      localStorage.setItem(STORAGE_KEY, Date.now().toString());
      return;
    }

    // Validate timestamp is a valid number
    const timestamp = parseInt(storedTimestamp, 10);
    if (isNaN(timestamp)) {
      // Invalid timestamp, reset to current time
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[Appointments] Invalid timestamp in localStorage, resetting');
      }
      localStorage.setItem(STORAGE_KEY, Date.now().toString());
      return;
    }

    const age = Date.now() - timestamp;

    if (age > EXPIRY_MS) {
      if (process.env.NODE_ENV !== 'production') {
        console.debug(
          `[Appointments] currentDate expired (${Math.round(age / 60000)} minutes old), resetting to today`,
        );
      }
      dispatch(setCurrentDate(new Date().toISOString()));
      localStorage.setItem(STORAGE_KEY, Date.now().toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Update timestamp whenever user manually changes the date
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
  }, [currentDate]);
}